import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { APP_NAME } from '@scriptto/shared';
import { useAuth } from '../hooks/useAuth';
import { useCredits } from '../hooks/useCredits';
import { useIsAdmin } from '../hooks/useIsAdmin';
import CreditsBar from './CreditsBar';

const navItems = [
  { to: '/dashboard', label: 'Gestione testate', end: true },
  { to: '/dashboard/voices', label: 'Voci' },
  { to: '/dashboard/avatars', label: 'Avatar', disabled: true },
];

export default function PageLayout() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { credits, refresh: refreshCredits } = useCredits();
  const isAdmin = useIsAdmin();

  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      {/* Sidebar desktop / topbar mobile */}
      <aside className="shrink-0 border-b border-slate-200 bg-white md:w-64 md:border-b-0 md:border-r">
        <div className="flex h-14 items-center border-b border-slate-100 px-4 md:h-16 md:px-6">
          <span className="text-sm font-bold tracking-tight text-brand-600">{APP_NAME}</span>
        </div>
        <nav className="flex overflow-x-auto p-2 md:flex-col md:gap-1 md:p-4">
          {navItems.map((item) =>
            item.disabled ? (
              <span
                key={item.to}
                title="Disponibile a breve"
                className="flex cursor-not-allowed items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-slate-400 opacity-60"
              >
                {item.label}
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                  Coming soon
                </span>
              </span>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition ${
                    isActive
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>
        <div className="hidden md:block md:px-4 md:pb-4 space-y-1">
          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `block w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition ${
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
            >
              Admin
            </NavLink>
          )}
          <button
            onClick={handleLogout}
            className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
          >
            Esci
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 bg-slate-50 p-4 md:p-8">
        <CreditsBar credits={credits} />
        <Outlet context={{ credits, refreshCredits }} />
      </main>
    </div>
  );
}

/** Tipo del context esposto da <Outlet /> ai figli (Dashboard, ProductionDetail). */
export interface PageOutletContext {
  credits: ReturnType<typeof useCredits>['credits'];
  refreshCredits: ReturnType<typeof useCredits>['refresh'];
}
